import React from 'react';
import { ChatMessage } from '../../utils/mockData'; // Adjust path as needed
import {
  Box,
  Paper,
  Typography,
  Avatar,
  useTheme,
  Stack,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person'; // For user
import SmartToyIcon from '@mui/icons-material/SmartToy'; // For assistant/bot
import { format } from 'date-fns';

interface ChatMessageViewProps {
  message: ChatMessage;
}

const ChatMessageView: React.FC<ChatMessageViewProps> = ({ message }) => {
  const theme = useTheme();
  const isUser = message.sender === 'user';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 1.5,
      }}
    >
      <Stack direction={isUser ? "row-reverse" : "row"} spacing={1} alignItems="flex-end" maxWidth="80%">
        <Avatar
          sx={{
            bgcolor: isUser ? theme.palette.primary.main : theme.palette.secondary.main,
            width: 32,
            height: 32,
            fontSize: '1rem', // Smaller avatar
          }}
        >
          {isUser ? <PersonIcon fontSize="small" /> : <SmartToyIcon fontSize="small" />}
        </Avatar>
        <Paper
          elevation={1} // Keep a subtle shadow
          sx={{
            px: 1.75, // Horizontal padding (14px if 8px base)
            py: 1.25, // Vertical padding (10px if 8px base)
            borderRadius: '20px', // General roundedness
            borderTopRightRadius: isUser ? '4px' : '20px', // User message has sharper top-right
            borderTopLeftRadius: isUser ? '20px' : '4px', // Assistant message has sharper top-left
            bgcolor: isUser
                ? theme.palette.primary.main
                : (theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[700]),
            color: isUser
                ? theme.palette.primary.contrastText
                : theme.palette.text.primary,
            overflowWrap: 'break-word',
            wordWrap: 'break-word',
            hyphens: 'auto',
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{message.text}</Typography>
          <Typography
            variant="caption"
            component="div" // Use div for block display
            sx={{
              mt: 0.5, // Keep margin-top
              textAlign: 'inherit', // Inherit textAlign from parent Stack/Box for proper alignment
              display: 'block', // Ensure it takes its own line
              color: isUser ? theme.palette.primary.contrastText : theme.palette.text.secondary, // Adjust color for better readability
              opacity: isUser ? 0.85 : 0.7, // Slightly different opacity
              fontSize: '0.7rem', // Keep small font size
            }}
          >
            {format(message.timestamp, 'p')} {/* e.g., 10:30 AM */}
          </Typography>
        </Paper>
      </Stack>
    </Box>
  );
};

export default ChatMessageView;
